import { Injectable } from '@angular/core';
import { ApiWebSubmissionService } from '@core/services/api-web-submission.service';
import { ApiResponse } from '@src/app/core/models/api-response.model';
import { SysParameter } from '@src/app/core/models/sys-parameter.model';
import { isNullOrEmpty } from 'projects/sigv-library/src/util/src/public-api';
import { Observable } from 'rxjs';
import { UserProfileService } from 'sigv-library';
import { CalculateNPVAndIRRReq } from '../models/calculate-npv-and-irr-req.model';
import { CalculateNPVAndIRR } from '../models/calculate-npv-and-irr.model';
import { CalculatePmtForIRRReq } from '../models/calculate-pmt-for-irr-req.model';
import { CalculatePmtForIRR } from '../models/calculate-pmt-for-irr.model';
import { CalculatePmtReq } from '../models/calculate-pmt-req.model';
import { CalculatePmt } from '../models/calculate-pmt.model';
import { ExpenseChargingReq } from '../models/expense-charging-req';
import { ExpenseChargingRes } from '../models/expense-charging-res';
import { MykadContent } from '../models/mykad-content.model';
import { ProductInfo } from '../models/product-info.model';
import { ProductVersionInfo } from '../models/product-version-info.model';
import { QuerySubmissionListRes } from '../models/query-submission-list-res.model';
import { SettlementContractNoRes } from '../models/settlement-contract-no-res.model';
import { SystemParameter } from '../models/system-parameter.model';
import { TaxCalcReq } from '../models/tax-calc-req.model';
import { TaxCalcRes } from '../models/tax-calc-res.model';

@Injectable()
export class CommonService {
  constructor(
    private apiWebSubmissionService: ApiWebSubmissionService,
    private userService: UserProfileService
  ) {}

  /** 取得產品參數最新版本 */
  getProductVersion(
    companyId: string
  ): Observable<ApiResponse<ProductVersionInfo[]>> {
    return this.apiWebSubmissionService.doGet(
      `/Common/GetProduct/${companyId}`
    );
  }

  /**
   * @param version 欲取得最新版本帶 0，草稿部分帶草稿本身的版本號
   */
  getProductCode(
    companyId: string,
    productCode: string,
    version: number
  ): Observable<ApiResponse<ProductInfo>> {
    return this.apiWebSubmissionService.doGet(
      `/Common/GetProduct/${companyId}/${productCode}/${version}`
    );
  }

  querySubmissionList(
    idNo: string
  ): Observable<ApiResponse<QuerySubmissionListRes[]>> {
    return this.apiWebSubmissionService.doPost('/Common/QuerySubmissionList', {
      idNo,
    });
  }

  /** 查詢契約是否已結清報價核定 */
  checkSettlementContractNo(
    idNo: string,
    settlementContractNo: string,
    companyId: number
  ): Observable<ApiResponse<SettlementContractNoRes>> {
    return this.apiWebSubmissionService.doPost(
      '/Common/CheckSettlementContractNo',
      {
        idNo,
        settlementContractNo,
        companyId,
      }
    );
  }

  /** 計算IRR NPV */
  calculateNPVAndIRR(
    req: CalculateNPVAndIRRReq,
    companyId: string
  ): Observable<ApiResponse<CalculateNPVAndIRR>> {

    if(isNullOrEmpty(companyId)){
      companyId = this.userService.currentUserValue?.companyId ?? '';
    }
    
    return this.apiWebSubmissionService.doPost(
      '/Common/CalculateNPVAndIRR',
      req,
      {
        'X-CompanyId': companyId ? companyId : '',
      }
    );
  }

  /** 取得期款(反推) */
  calculatePmtForIRR(
    req: CalculatePmtForIRRReq,
    companyId: string
  ): Observable<ApiResponse<CalculatePmtForIRR>> {

    if (isNullOrEmpty(companyId)) {
      companyId = this.userService.currentUserValue?.companyId ?? '';
    }

    return this.apiWebSubmissionService.doPost(
      '/Common/CalculatePmtForIRR',
      req,
      {
        'X-CompanyId': companyId ? companyId : '',
      }
    );
  }

  /** 取得期款 */
  calculatePmt(req: CalculatePmtReq): Observable<ApiResponse<CalculatePmt>> {
    return this.apiWebSubmissionService.doPost('Common/CalculatePmt', req);
  }

  taxCalculate(req: TaxCalcReq): Observable<ApiResponse<TaxCalcRes>> {
    return this.apiWebSubmissionService.doPost('Common/TaxCalc', req);
  }

  getAllSysParameters(
    companyId: string
  ): Observable<ApiResponse<SysParameter[]>> {
    return this.apiWebSubmissionService.doGet(
      '/Common/GetAllSysParameters',
      undefined,
      { 'X-CompanyId': companyId }
    );
  }

  getManySysParameters(
    companyId: string,
    types: string[]
  ): Observable<ApiResponse<SysParameter[]>> {
    return this.apiWebSubmissionService.doPost(
      '/Common/GetManySysParameters',
      types,
      {
        'X-CompanyId': companyId,
      }
    );
  }

  getSystemParameter(
    typeCode: string
  ): Observable<ApiResponse<SystemParameter[]>> {
    return this.apiWebSubmissionService.doGet(
      `/Common/GetSystemParameter/${typeCode}`
    );
  }

  getSystemParameterByParent(
    parentCode: string
  ): Observable<ApiResponse<SystemParameter[]>> {
    return this.apiWebSubmissionService.doGet(
      `/Common/GetSystemParameterByParent/${parentCode}`
    );
  }

  genIdentificationForm(
    content: MykadContent
  ): Observable<ApiResponse<{ fileDetailId: string }>> {
    return this.apiWebSubmissionService.doPost(
      '/Common/DownloadIdentificationPDF',
      content
    );
  }

  // 級距收費金額對照表
  getExpenseCharging(
    req: ExpenseChargingReq
  ): Observable<ApiResponse<ExpenseChargingRes>> {
    const companyId = this.userService.currentUserValue?.companyId;
    return this.apiWebSubmissionService.doPost(
      '/Common/ExpenseCharging',
      req,
      {
        'X-CompanyId': companyId ? companyId : '',
      }
    );
  }
}
